'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { RHFShadcnTextField } from '@/components/RHF';
import FormProvider from '@/components/RHF/FormProvider';
import { Button } from '@/components/ui/button';
import { frontEndRoutes } from '@/constants/routes';
import { AuthLayout } from '@/containers/layouts/AuthLayout';
import { sendPasswordResetLink } from '@/services/firebaseServices/AuthServices/authentication';

type ForgotPasswordFormFields = {
  email: string;
};

export function ForgotPasswordPage() {
  const translate = useTranslations();
  const router = useRouter();
  const [successMessage, setSuccessMessage] = useState('');

  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .email(translate('auth.validation.emailInvalid')),
  });

  const methods = useForm<ForgotPasswordFormFields>({
    mode: 'onChange',
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const { handleSubmit, formState: { isSubmitting } } = methods;

  const onSubmit = async (data: ForgotPasswordFormFields) => {
    setSuccessMessage('');
    await sendPasswordResetLink(data.email);
    setSuccessMessage(translate('auth.forgotPassword.emailSent'));
    setTimeout(() => {
      router.push(frontEndRoutes.auth.signIn);
    }, 5000);
  };

  return (
    <AuthLayout>
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate('auth.forgotPassword.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate('auth.forgotPassword.description')}
        </p>
      </div>

      <FormProvider methods={methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <RHFShadcnTextField
            name="email"
            title={translate('auth.forgotPassword.emailLabel')}
            fieldType="email"
            startIcon="material-symbols:mail"
            disabled={isSubmitting}
          />

          {successMessage.length > 0 && (
            <div className="rounded-md border border-success bg-success/10 p-3">
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {translate('auth.forgotPassword.submit')}
          </Button>
        </form>
      </FormProvider>

      <div className="text-center">
        <Link
          href={frontEndRoutes.auth.signIn}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          {translate('auth.forgotPassword.backToSignIn')}
        </Link>
      </div>
    </AuthLayout>
  );
}
